import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { toast } from "sonner";
import {
  Loader2, Plus, BellRing, Trash2, Edit3, Play, DollarSign, ShieldAlert, LayoutDashboard,
  Mail, Clock, ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const METRIC_META = {
  ai_cost_usd: { label: "AI spend (USD)", icon: DollarSign, tint: "#34D399", unit: "$", window_unit: "days" },
  failed_logins: { label: "Failed logins", icon: ShieldAlert, tint: "#F87171", unit: "", window_unit: "hours" },
  dashboard_activity: { label: "Dashboard activity", icon: LayoutDashboard, tint: "#22D3EE", unit: "", window_unit: "days" },
};

function ago(ts) {
  if (!ts) return "never";
  const s = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(ts).toLocaleString();
}

export default function SAAlerts() {
  const { sa: me } = useSuperAdminAuth();
  const { tenants } = useSuperAdminTenant();
  const [rules, setRules] = useState(null);
  const [history, setHistory] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode, row? }

  const load = async () => {
    try {
      const [r, h] = await Promise.all([
        saApi.get("/alerts"),
        saApi.get("/alerts/history"),
      ]);
      setRules(r.data.items || []);
      setHistory(h.data.items || []);
    } catch (e) {
      setRules([]); setHistory([]);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async (r) => {
    try {
      const { data } = await saApi.post(`/alerts/${r.id}/test`);
      if (data.fired) {
        const delivered = (data.delivered_to || []).length;
        const targetsConfigured = (r.email_targets || []).length > 0;
        if (delivered) {
          toast.success(`Fired — emailed ${delivered} recipient(s)`);
        } else if (targetsConfigured) {
          toast.info("Fired — email delivery failed (check SMTP config)");
        } else {
          toast.info("Fired — no recipients configured");
        }
      } else if (data.reason === "below_threshold") {
        toast.info(`Not fired — value ${data.value} ≤ threshold ${data.threshold}`);
      } else {
        toast.info(`Not fired: ${data.reason || "unknown"}${data.value != null ? ` (value ${data.value})` : ""}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await saApi.delete(`/alerts/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleEnabled = async (r) => {
    try { await saApi.put(`/alerts/${r.id}`, { enabled: !r.enabled }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <SuperAdminShell
      title="Alert Rules"
      actions={me?.is_master && (
        <Button data-testid="sa-alerts-create" onClick={() => setDialog({ mode: "create" })}
                className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
          <Plus className="w-4 h-4 mr-1.5" />New Rule
        </Button>
      )}
    >
      <div className="px-5 md:px-8 py-6 space-y-6">
        {rules === null ? (
          <div className="flex items-center gap-2 text-white/60"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : rules.length === 0 ? (
          <div className="rounded-2xl p-10 border border-white/10 text-center text-white/50"
               style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))" }}>
            <BellRing className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <div className="text-white text-lg font-display mb-1">No alert rules yet</div>
            <div className="text-sm text-white/50 max-w-md mx-auto">
              Turn any insight (AI spend, failed-login spike, dashboard activity) into a saved rule
              that emails support when the threshold is crossed. Evaluated every minute.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rules.map((r) => {
              const meta = METRIC_META[r.metric] || {};
              const Icon = meta.icon || BellRing;
              return (
                <div key={r.id} data-testid="sa-alert-card"
                     className="rounded-2xl p-5 border border-white/10 backdrop-blur-md"
                     style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                         style={{ background: `${meta.tint || "#22D3EE"}22` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.tint || "#22D3EE" }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-white font-medium truncate">{r.name}</div>
                        <Badge className={`text-[10px] ${r.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"} border-0`}>
                          {r.enabled ? "Active" : "Paused"}
                        </Badge>
                      </div>
                      <div className="text-[11px] text-white/50 uppercase tracking-widest">
                        {meta.label || r.metric} &gt; {meta.unit}{r.threshold}
                        {" · "}window {r.window} {meta.window_unit || ""}
                        {" · "}{r.tenant_id ? tenants.find((t) => t.id === r.tenant_id)?.name || r.tenant_id : "all tenants"}
                      </div>
                      <div className="mt-2 text-[11px] text-white/50 flex flex-wrap gap-3">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{(r.email_targets || []).length} recipient(s)</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />last fired {ago(r.last_fired_at)}</span>
                        {r.last_value != null && <span>last value {meta.unit}{r.last_value}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 justify-end">
                    <Switch data-testid={`sa-alert-toggle-${r.id}`} checked={!!r.enabled}
                            onCheckedChange={() => toggleEnabled(r)} className="mr-2 scale-90" />
                    <Button data-testid={`sa-alert-test-${r.id}`} size="sm" variant="ghost"
                            onClick={() => runNow(r)}
                            className="text-cyan-300 hover:text-white hover:bg-cyan-400/10 h-8">
                      <Play className="w-3.5 h-3.5 mr-1" />Test
                    </Button>
                    {me?.is_master && (
                      <>
                        <Button data-testid={`sa-alert-edit-${r.id}`} size="sm" variant="ghost"
                                onClick={() => setDialog({ mode: "edit", row: r })}
                                className="text-white/70 hover:text-white hover:bg-white/5 h-8">
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`sa-alert-delete-${r.id}`} size="sm" variant="ghost"
                                onClick={() => doDelete(r)}
                                className="text-white/70 hover:text-red-400 hover:bg-red-500/10 h-8">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* History */}
        <div className="rounded-2xl border border-white/10 backdrop-blur-md overflow-hidden"
             style={{ background: "rgba(255,255,255,0.02)" }}>
          <button data-testid="sa-alerts-history-toggle" onClick={() => setExpanded((v) => !v)}
                  className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-white/5 transition">
            <span className="text-[10px] uppercase tracking-widest text-white/50">Fire history</span>
            <span className="flex items-center gap-2 text-white/70 text-sm">
              {(history || []).length} entries {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          </button>
          {expanded && (
            <ol className="divide-y divide-white/5">
              {(history || []).length === 0 ? (
                <li className="px-5 py-4 text-white/40 text-sm">No alerts have fired yet.</li>
              ) : history.map((h) => (
                <li key={h.id} data-testid="sa-alert-history-row"
                    className="px-5 py-2.5 flex items-center gap-3 text-sm">
                  <span className="text-white/90 flex-1 min-w-0 truncate">{h.rule_name}</span>
                  <span className="text-xs text-white/60 font-mono">{h.metric} = {h.value} (&gt;{h.threshold})</span>
                  <span className="text-[10px] text-white/40 uppercase tracking-widest w-24 text-right">{ago(h.ts)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {dialog && (
        <AlertRuleDialog
          mode={dialog.mode}
          row={dialog.row}
          tenants={tenants}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </SuperAdminShell>
  );
}

function AlertRuleDialog({ mode, row, tenants, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    name: row?.name || "",
    metric: row?.metric || "ai_cost_usd",
    threshold: row?.threshold != null ? String(row.threshold) : "10",
    window: row?.window != null ? String(row.window) : "30",
    tenant_id: row?.tenant_id || "",
    email_targets_text: (row?.email_targets || []).join(", "),
    cooldown_minutes: String(row?.cooldown_minutes || 360),
    enabled: row?.enabled !== false,
  }));
  const [saving, setSaving] = useState(false);

  const meta = METRIC_META[form.metric] || {};

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      metric: form.metric,
      threshold: parseFloat(form.threshold) || 0,
      window: parseInt(form.window, 10) || 1,
      tenant_id: form.tenant_id || null,
      email_targets: form.email_targets_text.split(",").map((s) => s.trim()).filter(Boolean),
      cooldown_minutes: parseInt(form.cooldown_minutes, 10) || 360,
      enabled: !!form.enabled,
    };
    try {
      if (isEdit) await saApi.put(`/alerts/${row.id}`, body);
      else await saApi.post("/alerts", body);
      toast.success(isEdit ? "Rule updated" : "Rule created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit alert rule" : "New alert rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Name</Label>
            <Input data-testid="sa-alert-form-name" className="mt-1.5"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="AI spend spike" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Metric</Label>
              <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                <SelectTrigger data-testid="sa-alert-form-metric" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(METRIC_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tenant scope</Label>
              <Select value={form.tenant_id || "__all__"} onValueChange={(v) => setForm({ ...form, tenant_id: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="sa-alert-form-tenant" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All tenants</SelectItem>
                  {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Threshold ({meta.unit || "count"})</Label>
              <Input data-testid="sa-alert-form-threshold" className="mt-1.5"
                     type="number" min="0" step="0.01"
                     value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
            </div>
            <div>
              <Label>Window ({meta.window_unit || "days"})</Label>
              <Input data-testid="sa-alert-form-window" className="mt-1.5"
                     type="number" min="1" max="90"
                     value={form.window} onChange={(e) => setForm({ ...form, window: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Email recipients</Label>
            <Input data-testid="sa-alert-form-emails" className="mt-1.5"
                   value={form.email_targets_text}
                   onChange={(e) => setForm({ ...form, email_targets_text: e.target.value })}
                   placeholder="ops@example.com, support@example.com" />
            <p className="text-[11px] text-gray-500 mt-1">Comma-separated. Falls back to Global Settings&apos; support_email if empty.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label>Cooldown (minutes)</Label>
              <Input data-testid="sa-alert-form-cooldown" className="mt-1.5"
                     type="number" min="1"
                     value={form.cooldown_minutes} onChange={(e) => setForm({ ...form, cooldown_minutes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg p-2.5 border">
              <Label className="text-sm">Enabled</Label>
              <Switch data-testid="sa-alert-form-enabled" checked={form.enabled}
                      onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="sa-alert-form-submit" onClick={submit} disabled={saving}
                  className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save" : "Create rule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
